import React, { useMemo, useState } from "react";
import {
  FaSync,
  FaFire,
  FaCoffee,
  FaCog,
  FaMugHot,
  FaShieldAlt,
  FaBroom,
  FaTint,
  FaRedo,
  FaChevronDown,
  FaChevronRight,
  FaSave,
  FaTimes,
} from "react-icons/fa";
import { useDispatch } from "react-redux";
import {
  useReadOkcSettingsQuery,
  useWriteOkcSettingsMutation,
  devicesSettingsV2Api,
} from "../../../../store/storageRoles/devicesSettingsV2Api";
import useAuth from "../../../../Hooks/useAuth";
import EmptyState from "../../../shared/EmptyState";
import FilterTabs from "../../../shared/FilterTabs";
import SearchInput from "../../../shared/SearchInput";
import SettingsFieldCard from "../settingsTabV2/SettingsFieldCard";
import SettingsSkeleton from "../settingsTabV2/SettingsSkeleton";
import { SettingsSubgroupHeader } from "../settingsTabV2/SettingsSectionShell";
import { TopicHeader } from "../settingsTabV2/GeneralSection";
import { pushTimedAlert, settingsSaveAlert } from "../../../../utils/settingsSaveAlert";
import {
  changeId,
  itemValue,
  toFieldSetting,
  normalizeValue,
  validateValue,
  formatValue,
  reasonText,
  buildChanges,
  applyAccepted,
  changeTitle,
  topicBlocks,
  isShownForAutoMixer,
  matchesSearch,
} from "./okcSettingsModel";

/**
 * OKC (eski kart seti Türk kahvesi) Ayarlar sekmesi — #325, görünüm #364.
 * Sözleşme: BayinerIOT-Workspace-/docs/api-contracts/okc-settings.md (1.2)
 *
 * Bölüm, konu ve etiketler sunucudan gelir ve yeni makinenin (settings v2)
 * sekmesiyle aynıdır; kabuk da onun kabuğu (kategori şeridi, arama, alt grup
 * başlıkları, göz kartları). Eski kartta pişirme ayarları fincana ve moda
 * göre ayrılmaz — göz başına tek değer.
 */
// SettingsTabV2 SECTION_DEFS ile aynı ikon ve renk.
const SECTION_META = {
  settings: { icon: FaCog, color: "blue", subgroup: "Sistem" },
  general: { icon: FaFire, color: "emerald", subgroup: "Türk Kahvesi" },
  pots: { icon: FaCoffee, color: "amber" },
};

const TOPIC_ICONS = {
  "Güvenlik": FaShieldAlt,
  "Sistem": FaCog,
  "Türk Kahvesi Ayarı": FaMugHot,
  "Temizlik Ayarı": FaBroom,
  "Su Deposu Ayarı": FaTint,
  "Değirmen Ayarı": FaCog,
  "Türk Kahvesi": FaMugHot,
  "Fincanlar": FaCoffee,
  "Türk Kahvesi Pişirme Ayarı": FaFire,
  "Otomatik Karıştırma Eğrisi": FaRedo,
};

const GRID = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3";

// CustomPotSection PotBlock'un Türk kahvesi başlığı.
function PotCard({ cup, total, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-amber-200 bg-white mb-3 last:mb-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2.5 flex items-center gap-2 text-left bg-gradient-to-r from-amber-50 to-yellow-50 hover:from-amber-100 hover:to-yellow-100"
      >
        {open ? (
          <FaChevronDown className="text-amber-700" />
        ) : (
          <FaChevronRight className="text-amber-700" />
        )}
        <FaMugHot className="text-amber-700" />
        <span className="text-sm font-Bold text-amber-900">{cup + 1}. Göz Türk Kahvesi</span>
        <span className="ml-auto text-[11px] font-Medium px-2 py-0.5 rounded-md border bg-amber-100 text-amber-700 border-amber-200">
          {total} alan
        </span>
      </button>
      {open && <div className="p-3 space-y-3 border-t border-gray-200">{children}</div>}
    </div>
  );
}

export default function OkcSettingsTab({ selectedDevice, setAlerts, isDrawer = false }) {
  const { auth } = useAuth();
  const dispatch = useDispatch();
  const id = selectedDevice?._id;
  const accessToken = auth?.accessToken;
  const queryArgs = useMemo(() => ({ id, token: accessToken }), [id, accessToken]);
  const { data, isLoading, isFetching, error, refetch } = useReadOkcSettingsQuery(queryArgs, {
    skip: !id || !accessToken,
  });
  const [writeMutation, { isLoading: isWriting }] = useWriteOkcSettingsMutation();

  const [activeGroup, setActiveGroup] = useState(null);
  // { [changeId]: { key, cup?, value } }
  const [pending, setPending] = useState({});
  const [results, setResults] = useState(null);
  const [search, setSearch] = useState("");

  const groups = useMemo(() => (Array.isArray(data?.groups) ? data.groups : []), [data]);
  const cupCount = data?.cupCount || 1;
  const itemsByKey = useMemo(() => {
    const map = new Map();
    groups.forEach((g) => g.items.forEach((it) => map.set(it.key, it)));
    return map;
  }, [groups]);

  const pendingList = Object.values(pending);
  const invalid = pendingList
    .map((c) => {
      const item = itemsByKey.get(c.key);
      const err = item ? validateValue(item, c.value) : "Bilinmeyen ayar";
      return err ? { ...c, item, err } : null;
    })
    .filter(Boolean);

  const handleChange = (item, cup, raw) => {
    const value = normalizeValue(item, raw);
    const cid = changeId(item.key, cup);
    setPending((prev) => {
      const next = { ...prev };
      if (value === itemValue(item, cup)) delete next[cid];
      else next[cid] = cup === undefined ? { key: item.key, value } : { key: item.key, cup, value };
      return next;
    });
  };

  const handleRefresh = () => {
    setPending({});
    setResults(null);
    refetch();
  };

  const handleSave = async () => {
    const changes = buildChanges(pending);
    try {
      const resp = await writeMutation({ id, changes, token: accessToken }).unwrap();
      const items = Array.isArray(resp?.results) ? resp.results : [];
      dispatch(
        devicesSettingsV2Api.util.updateQueryData("readOkcSettings", queryArgs, (draft) =>
          applyAccepted(draft, changes, items),
        ),
      );
      setResults(resp);
      setPending({});
      // Sonuç kutusu uzun listenin altında kalıp görünmüyordu — uyarı (#258)
      const saveAlert = settingsSaveAlert(resp);
      pushTimedAlert(setAlerts, saveAlert.message, saveAlert.severity);
    } catch (err) {
      pushTimedAlert(setAlerts, err?.data?.message || "Kaydetme başarısız", "error");
    }
  };

  if (isLoading || (!data && isFetching) || isWriting) {
    return (
      <SettingsSkeleton
        message={
          isWriting
            ? "Ayarlar makineye yazılıyor..."
            : "Makine ayarları yükleniyor..."
        }
      />
    );
  }
  if (error) {
    const isOffline = error?.status === 504;
    return (
      <div className="flex flex-col items-center pb-6">
        <EmptyState
          message={isOffline ? "Cihaz çevrimdışı" : "Ayarlar yüklenemedi"}
          suggestion={
            isOffline
              ? "Cihaz şu an cevap vermiyor. Çevrimiçi olduğunda tekrar deneyin."
              : error?.data?.message || "Bilinmeyen hata"
          }
        />
        <button
          type="button"
          onClick={handleRefresh}
          className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-SemiBold shadow-sm hover:bg-gray-50"
        >
          <FaSync className="w-4 h-4" />
          Tekrar Dene
        </button>
      </div>
    );
  }
  if (groups.length === 0) {
    return (
      <EmptyState
        message="Görüntülenecek ayar yok"
        suggestion="Bu makinede görebileceğiniz bir ayar bulunmuyor."
      />
    );
  }

  const active = groups.find((g) => g.key === activeGroup) || groups[0];
  const meta = SECTION_META[active.key] || { icon: FaCog, color: "indigo" };
  const pendingByGroup = {};
  pendingList.forEach((c) => {
    const g = groups.find((gr) => gr.items.some((it) => it.key === c.key));
    if (g) pendingByGroup[g.key] = (pendingByGroup[g.key] || 0) + 1;
  });
  const fieldCount = (g) =>
    g.items.reduce((n, it) => n + (it.scope === "cup" ? cupCount : 1), 0);
  const totalFields = groups.reduce((n, g) => n + fieldCount(g), 0);

  const currentValue = (item, cup) => {
    const cid = changeId(item.key, cup);
    return cid in pending ? pending[cid].value : itemValue(item, cup);
  };

  const renderCard = (item, cup) => {
    const cid = changeId(item.key, cup);
    const original = itemValue(item, cup);
    const isChanged = cid in pending;
    return (
      <SettingsFieldCard
        key={cid}
        setting={toFieldSetting(item, currentValue(item, cup))}
        onChange={(v) => handleChange(item, cup, v)}
        isChanged={isChanged}
        originalValue={isChanged ? formatValue(item, original) : undefined}
        onRevert={() =>
          setPending((prev) => {
            const next = { ...prev };
            delete next[cid];
            return next;
          })
        }
        color={meta.color}
      />
    );
  };

  const searched = active.items.filter((it) => matchesSearch(it, search, active.label));

  const renderBlocks = (items, cup) =>
    topicBlocks(items).map((block) => (
      <div key={block.topic || "all"} className="mb-4 last:mb-0">
        {block.topic && (
          <TopicHeader
            icon={TOPIC_ICONS[block.topic]}
            title={block.topic}
            count={block.items.length}
          />
        )}
        <div className={GRID}>{block.items.map((it) => renderCard(it, cup))}</div>
      </div>
    ));

  const renderSection = () => {
    if (searched.length === 0) {
      return (
        <EmptyState
          message="Sonuç bulunamadı"
          suggestion={`'${search}' için eşleşen alan yok`}
        />
      );
    }
    const machineItems = searched.filter((it) => it.scope !== "cup");
    const cupItems = searched.filter((it) => it.scope === "cup");
    const autoMixerItem = itemsByKey.get("autoMixer");
    return (
      <div className="space-y-3">
        {machineItems.length > 0 && (
          <div>
            {meta.subgroup && (
              <SettingsSubgroupHeader
                icon={meta.icon}
                title={meta.subgroup}
                count={machineItems.length}
                color={meta.color}
              />
            )}
            {renderBlocks(machineItems)}
          </div>
        )}
        {cupItems.length > 0 &&
          Array.from({ length: cupCount }, (_, cup) => {
            const autoMixer = autoMixerItem ? currentValue(autoMixerItem, cup) : undefined;
            const shown = cupItems.filter((it) => isShownForAutoMixer(it, autoMixer));
            return (
              <PotCard key={cup} cup={cup} total={shown.length}>
                {renderBlocks(shown, cup)}
              </PotCard>
            );
          })}
      </div>
    );
  };

  const failed = Array.isArray(results?.results)
    ? results.results.filter((r) => r.success !== true)
    : [];

  return (
    <div className={`flex flex-col ${isDrawer ? "h-full" : ""}`}>
      <div className="px-3 sm:px-4 pt-3 pb-3 space-y-2.5 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <FilterTabs
              filters={groups.map((g) => ({
                id: g.key,
                label: g.label,
                icon: (SECTION_META[g.key] || {}).icon,
                count: fieldCount(g),
                dot: (pendingByGroup[g.key] || 0) > 0,
              }))}
              activeFilter={active.key}
              onChange={setActiveGroup}
              totalCount={fieldCount(active)}
              color={meta.color}
            />
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-gray-200 bg-gray-50 text-xs text-gray-500 whitespace-nowrap">
              <FaCog className="w-3.5 h-3.5 text-indigo-500" />
              <span className="font-SemiBold text-gray-700">{totalFields}</span>
              alan
            </span>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={isFetching}
              title="Yenile"
              className="inline-flex items-center gap-2 shrink-0 h-9 px-2.5 sm:px-3 rounded-lg border border-gray-300 bg-white text-gray-600 text-sm font-SemiBold shadow-sm hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 transition-colors"
            >
              <FaSync className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`} />
              <span className="hidden md:inline">Yenile</span>
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="w-full max-w-md">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`${active.label} içinde ara...`}
            />
          </div>
          {data?.firmware && (
            <p className="text-[11px] text-gray-400">Ekran yazılımı v{data.firmware}</p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3">{renderSection()}</div>

      {results && (
        <div className="mx-3 sm:mx-4 mb-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-SemiBold text-gray-700">
              {results.summary?.accepted ?? 0} ayar kaydedildi
              {failed.length > 0 && (
                <span className="text-fifth"> · {failed.length} ayar kaydedilemedi</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setResults(null)}
              className="text-gray-400 hover:text-gray-600"
              title="Kapat"
            >
              <FaTimes size={12} />
            </button>
          </div>
          {failed.length > 0 && (
            <ul className="mt-2 space-y-1">
              {failed.map((r) => {
                const item = itemsByKey.get(r.key);
                return (
                  <li key={changeId(r.key, r.cup)} className="text-xs text-gray-600">
                    {item ? changeTitle(item, r.cup) : r.key}:{" "}
                    <span className="text-fifth">{reasonText(r.reason)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {pendingList.length > 0 && (
        <div className="sticky bottom-0 mx-3 sm:mx-4 mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 shadow-md">
          <div className="flex flex-wrap items-center gap-2">
            <p className="flex-1 min-w-0 text-sm text-amber-800">
              <span className="font-SemiBold">{pendingList.length}</span> değişiklik
              {invalid.length > 0 && (
                <span className="text-fifth"> · {invalid.length} hatalı</span>
              )}
            </p>
            <button
              type="button"
              onClick={() => setPending({})}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-300 bg-white text-sm text-gray-600 hover:bg-gray-50"
            >
              <FaTimes className="w-3.5 h-3.5" />
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={invalid.length > 0 || pendingList.length > 40}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-main text-sm font-SemiBold text-white hover:bg-mainSecond disabled:opacity-50"
            >
              <FaSave className="w-3.5 h-3.5" />
              Makineye Kaydet
            </button>
          </div>
          {invalid.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {invalid.map((c) => (
                <li key={changeId(c.key, c.cup)} className="text-xs text-fifth">
                  {c.item ? changeTitle(c.item, c.cup) : c.key}: {c.err}
                </li>
              ))}
            </ul>
          )}
          {pendingList.length > 40 && (
            <p className="mt-2 text-xs text-fifth">
              Tek seferde en fazla 40 değişiklik kaydedilebilir.
            </p>
          )}
          <p className="mt-1 text-[11px] text-amber-700">
            Her ayar makinenin ekranına tek tek yazılır; kayıt birkaç saniye sürebilir.
          </p>
        </div>
      )}
    </div>
  );
}
